import { Component, type ErrorInfo, type ReactNode } from "react";

import i18n from "../../lib/i18n";
import { captureError } from "../../lib/sentry";

type Props = { children: ReactNode };
type State = { error: Error; info: ErrorInfo | null } | { error: null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
    captureError(error);
    this.setState({ error, info });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-surface-sunk p-4">
          <div className="max-w-md rounded-lg border border-line bg-surface p-6 text-center shadow-sm">
            <h1 className="mb-2 text-2xl font-bold text-fg">{i18n.t("boundary.title")}</h1>
            <p className="mb-4 text-sm text-fg-mute">{i18n.t("boundary.body")}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-bg-deep px-4 py-2 text-sm font-medium text-fg hover:bg-surface-raised"
            >
              {i18n.t("boundary.reload")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
